// Client-side intake session for portal lodging. Persists the active
// job and uploaded document references across refreshes in the browser session.
const SESSION_KEY = "solvtax_intake_session";
const JOBS_KEY = "solvtax_triage_jobs";
const MAX_TRACKED_JOBS = 10;

const createSessionId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `sess_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

export const getTrackedJobIds = () => {
  try {
    const raw = window.localStorage.getItem(JOBS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const trackJobId = (jobId) => {
  if (!jobId) return;
  const existing = getTrackedJobIds().filter((id) => id !== jobId);
  const next = [jobId, ...existing].slice(0, MAX_TRACKED_JOBS);
  try {
    window.localStorage.setItem(JOBS_KEY, JSON.stringify(next));
  } catch {
    // Ignore storage errors (e.g. private browsing quota); not critical.
  }
};

/**
 * @typedef {Object} IntakeSession
 * @property {string} sessionId
 * @property {string} jobId
 * @property {string} businessId
 * @property {string} assessmentNoticeUrl
 * @property {string} assetLedgerUrl
 * @property {string} assessmentFileName
 * @property {string} ledgerFileName
 */

/** @returns {IntakeSession | null} */
export const getIntakeSession = () => {
  try {
    const raw = window.localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || !parsed.jobId) return null;
    return parsed;
  } catch {
    return null;
  }
};

/** @param {Partial<IntakeSession> & { jobId: string }} session */
export const saveIntakeSession = (session) => {
  if (!session?.jobId) return;
  const existing = getIntakeSession();
  const next = {
    sessionId: existing?.sessionId ?? createSessionId(),
    businessId: "",
    assessmentNoticeUrl: "",
    assetLedgerUrl: "",
    assessmentFileName: "",
    ledgerFileName: "",
    ...existing,
    ...session,
    jobId: session.jobId,
  };
  try {
    window.localStorage.setItem(SESSION_KEY, JSON.stringify(next));
  } catch {
    // Non-critical for funnel UX.
  }
  trackJobId(session.jobId);
};

export const clearIntakeSession = () => {
  try {
    window.localStorage.removeItem(SESSION_KEY);
  } catch {
    // Ignore.
  }
};

export const getOrCreateSessionId = () => {
  const existing = getIntakeSession();
  if (existing?.sessionId) return existing.sessionId;
  return createSessionId();
};
